import { useState } from 'react';
import { ActionIcon, Badge, Button, Group, Stack, Table, Text, Title } from '@mantine/core';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import CompanyWrapper from 'src/company/templates/components/CompanyWrapper';
import { Company } from 'src/secret-menu/hooks/useCompanies';
import PageLoader from 'src/UI/loader/PageLoader';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { ApiError } from 'src/error/ApiError';
import CustomSkillEditor from './CustomSkillEditor';
import useCustomSkill from './hooks/useCustomSkill';
import useCustomSkills from './hooks/useCustomSkills';
import useDeleteCustomSkill from './hooks/useDeleteCustomSkill';
import type { CustomSkillListItem } from './types';

type EditorState = { mode: 'list' } | { mode: 'create' } | { mode: 'edit'; skillId: string };

export default function CustomSkillsTab() {
  return <CompanyWrapper contentComponent={CustomSkillsTabContent} />;
}

function CustomSkillsTabContent({ company }: { company: Company }) {
  const [editorState, setEditorState] = useState<EditorState>({ mode: 'list' });
  const [skillToDelete, setSkillToDelete] = useState<CustomSkillListItem | null>(null);
  const { data: skills, isLoading, isError } = useCustomSkills(company.id);
  const { mutateAsync: deleteSkill, isPending: isDeleting } = useDeleteCustomSkill(company.id);

  const editingSkillId = editorState.mode === 'edit' ? editorState.skillId : null;
  const { data: editingSkill } = useCustomSkill(company.id, editingSkillId);

  const handleConfirmDelete = async () => {
    if (!skillToDelete) return;
    try {
      await deleteSkill(skillToDelete.id);
      setSkillToDelete(null);
    } catch (error) {
      const detail =
        error instanceof ApiError ? error.message : 'An error occurred while deleting the skill.';
      notifyAndLogError('Failed to delete skill', detail, error);
    }
  };

  if (editorState.mode === 'create') {
    return (
      <Stack gap="lg" p="lg">
        <Title order={3}>New custom skill for {company.name}</Title>
        <CustomSkillEditor
          key="new"
          companyId={company.id}
          skill={null}
          onClose={() => setEditorState({ mode: 'list' })}
        />
      </Stack>
    );
  }

  if (editorState.mode === 'edit' && editingSkill) {
    return (
      <Stack gap="lg" p="lg">
        <Title order={3}>Edit custom skill for {company.name}</Title>
        <CustomSkillEditor
          key={editingSkill.id}
          companyId={company.id}
          skill={editingSkill}
          onClose={() => setEditorState({ mode: 'list' })}
        />
      </Stack>
    );
  }

  if (isLoading || (editorState.mode === 'edit' && !editingSkill)) {
    return <PageLoader absolute={false} />;
  }

  if (isError) {
    return (
      <Text c="red" p="lg">
        Failed to load custom skills.
      </Text>
    );
  }

  return (
    <Stack gap="lg" p="lg">
      <ConfirmDeleteModal
        opened={skillToDelete !== null}
        onClose={() => setSkillToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete custom skill"
        message={`Delete the '${skillToDelete?.name}' skill for ${company.name}? This cannot be undone.`}
        isLoading={isDeleting}
      />
      <Group justify="space-between" align="center">
        <div>
          <Title order={3}>Custom skills</Title>
          <Text size="sm" c="dimmed" mt="xs">
            Tenant-specific skills available to {company.name}. Each skill is available to Siesta
            during a chat conversation.
          </Text>
        </div>
        <Button size="xs" onClick={() => setEditorState({ mode: 'create' })}>
          Add custom skill
        </Button>
      </Group>

      {!skills?.length ? (
        <Text c="dimmed" fs="italic">
          No custom skills for {company.name} yet. Add one to get started.
        </Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Description</Table.Th>
              <Table.Th>Requires sandbox</Table.Th>
              <Table.Th>File explorer</Table.Th>
              <Table.Th>Company files</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Updated</Table.Th>
              <Table.Th w={80} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {skills.map(skill => (
              <Table.Tr key={skill.id}>
                <Table.Td>
                  <Text fw={500}>{skill.name}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {skill.description}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {skill.requiresSandbox ? (
                    <Badge size="xs" variant="light" color="blue">
                      Yes
                    </Badge>
                  ) : (
                    <Badge size="xs" variant="light" color="gray">
                      No
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  {skill.fileExplorerEnabled ? (
                    <Badge size="xs" variant="light" color="blue">
                      Yes
                    </Badge>
                  ) : (
                    <Badge size="xs" variant="light" color="gray">
                      No
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  {skill.companyFilesVisible ? (
                    <Badge size="xs" variant="light" color="blue">
                      Yes
                    </Badge>
                  ) : (
                    <Badge size="xs" variant="light" color="gray">
                      No
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  {skill.isEnabled ? (
                    <Badge size="xs" variant="light" color="green">
                      Enabled
                    </Badge>
                  ) : (
                    <Badge size="xs" variant="light" color="gray">
                      Disabled
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {skill.updatedAt ? new Date(skill.updatedAt).toLocaleDateString() : '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label={`Edit ${skill.name}`}
                      onClick={() => setEditorState({ mode: 'edit', skillId: skill.id })}
                    >
                      <IconPencil size={14} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="sm"
                      aria-label={`Delete ${skill.name}`}
                      onClick={() => setSkillToDelete(skill)}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}
